// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, ChevronDown, Plus, Link2 } from 'lucide-react';
import { useProcesses } from '@/api/queries/useProcesses';
import { useEditProcessChain } from '@/api/mutations/useProcessChains';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  Combobox,
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
  Badge,
  Button,
  Skeleton
} from '@bliks-io/ui';
import { AutoDetectDialog } from './AutoDetectDialog';
import { useProcessGraphHelpers } from './useProcessGraphHelpers';
import { filterProcessesBySearch } from '@/lib/process-utils';
import { LoadErrorRetry } from '@/components/load-error-retry/LoadErrorRetry';
import type { PopulatedChainProcess, ChainSwimlane } from '@bliks-io/contracts';

// TODO: Review AI Code for feature: Process Map - Entire file is AI-generated

/** Map a process category to a chain swimlane */
function mapCategoryToSwimlane(category?: string): ChainSwimlane {
  switch (category) {
    case 'management':
      return 'strategic';
    case 'core':
      return 'core';
    case 'support':
      return 'support';
    default:
      return 'core';
  }
}

interface MappingEntry {
  processId: string;
  processName: string;
  departmentName?: string;
}

interface EditProcessesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chainId: string;
  chainName: string;
  currentMappings: PopulatedChainProcess[];
}

export function EditProcessesDialog({
  open,
  onOpenChange,
  chainId,
  chainName,
  currentMappings
}: EditProcessesDialogProps) {
  const { t } = useTranslation();
  const { data: allProcesses, isLoading: processesLoading } = useProcesses();
  const editChain = useEditProcessChain();
  const { getSuggestions, processLookup, graphState } = useProcessGraphHelpers();

  const [mappings, setMappings] = useState<MappingEntry[]>(() =>
    currentMappings
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((m) => ({
        processId: m.processId,
        processName: m.processName,
        departmentName: m.departmentName
      }))
  );

  const [autoDetectOpen, setAutoDetectOpen] = useState(false);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);
  const [processSearch, setProcessSearch] = useState('');

  // Reset mappings when dialog opens
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setMappings(
          currentMappings
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((m) => ({
              processId: m.processId,
              processName: m.processName,
              departmentName: m.departmentName
            }))
        );
      }
      onOpenChange(nextOpen);
    },
    [currentMappings, onOpenChange]
  );

  const assignedIds = useMemo(() => new Set(mappings.map((m) => m.processId)), [mappings]);

  const availableProcesses = useMemo(
    () => (allProcesses ?? []).filter((p) => !assignedIds.has(p._id)),
    [allProcesses, assignedIds]
  );

  const comboboxProcesses = useMemo(
    () => filterProcessesBySearch(availableProcesses, processSearch),
    [availableProcesses, processSearch]
  );

  // Suggested processes: 1-hop neighbors not yet in this chain
  const suggestedProcesses = useMemo(() => {
    if (assignedIds.size === 0) return [];
    const suggestionIds = getSuggestions(assignedIds);
    return suggestionIds
      .map((id) => {
        const mapProc = processLookup.get(id);
        if (!mapProc) return null;
        return { _id: mapProc._id, name: mapProc.name, departmentName: mapProc.department.name };
      })
      .filter((p): p is NonNullable<typeof p> => p != null);
  }, [assignedIds, getSuggestions, processLookup]);

  const handleAddProcess = (processId: string | string[] | undefined) => {
    if (!processId || Array.isArray(processId)) return;
    const proc = allProcesses?.find((p) => p._id === processId);
    if (!proc) return;
    setMappings((prev) => [
      ...prev,
      { processId: proc._id, processName: proc.name, departmentName: proc.department?.name }
    ]);
  };

  const handleAddSuggestion = (processId: string) => {
    const proc = processLookup.get(processId);
    if (!proc) return;
    setMappings((prev) => [
      ...prev,
      { processId: proc._id, processName: proc.name, departmentName: proc.department.name }
    ]);
  };

  const handleRemove = (processId: string) => {
    setMappings((prev) => prev.filter((m) => m.processId !== processId));
  };

  const handleAutoDetectConfirm = (entries: MappingEntry[]) => {
    setMappings((prev) => [...prev, ...entries]);
    setAutoDetectOpen(false);
  };

  const handleSave = () => {
    editChain.mutate(
      {
        id: chainId,
        processSwimlaneMappings: mappings.map((m, i) => {
          const proc = processLookup.get(m.processId);
          const swimlane = mapCategoryToSwimlane(proc?.category);
          return {
            processId: m.processId,
            swimlane,
            order: i + 1
          };
        })
      },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogShell size="lg">
          <DialogShellHeader
            icon={<Link2 className="h-5 w-5" />}
            title={`${t('processChains.edit.title')} - ${chainName}`}
            subtitle={t('processChains.edit.description')}
            onClose={() => handleOpenChange(false)}
          />
          <DialogShellBody>
            <div className="flex flex-col gap-4">
              {/* Process Picker + Auto-Detect Button */}
              <div className="flex gap-2">
                <Combobox value={undefined} onValueChange={handleAddProcess}>
                  <ComboboxTrigger className="w-full">
                    {t('processChains.edit.addProcess')}
                  </ComboboxTrigger>
                  <ComboboxContent>
                    <ComboboxInput
                      placeholder={t('processChains.edit.addProcess')}
                      value={processSearch}
                      onValueChange={setProcessSearch}
                    />
                    <ComboboxList>
                      <ComboboxEmpty>
                        {processesLoading ? t('common.loading') : t('processes.empty')}
                      </ComboboxEmpty>
                      {comboboxProcesses.map((proc) => (
                        <ComboboxItem key={proc._id} value={proc._id}>
                          <div className="flex flex-col">
                            <span>{proc.name}</span>
                            {proc.department?.name && (
                              <span className="text-xs text-muted-foreground">
                                {proc.department.name}
                              </span>
                            )}
                          </div>
                        </ComboboxItem>
                      ))}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
                <Button
                  variant="outline"
                  className="shrink-0"
                  onClick={() => setAutoDetectOpen(true)}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  {t('processChains.edit.autoDetect.button')}
                </Button>
              </div>

              {/* Mapping List */}
              {mappings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('processChains.edit.noProcesses')}
                </p>
              ) : (
                <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                  {mappings.map((entry) => (
                    <div
                      key={entry.processId}
                      className="flex items-center gap-2 rounded-md border p-2"
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-sm font-medium truncate">{entry.processName}</span>
                        {entry.departmentName && (
                          <span className="text-xs text-muted-foreground truncate">
                            {entry.departmentName}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                        onClick={() => handleRemove(entry.processId)}
                        title={t('processChains.edit.remove')}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {graphState?.status === 'failed' && (
                <LoadErrorRetry message={t('common.errorOccurred')} onRetry={graphState.retry} />
              )}
              {graphState?.status === 'pending' && (
                <div role="status" aria-label={t('common.loading')}>
                  <Skeleton className="h-10 w-full" />
                </div>
              )}

              {/* Suggested processes */}
              {suggestedProcesses.length > 0 && (
                <div className="border rounded-md">
                  <button
                    type="button"
                    onClick={() => setSuggestionsExpanded((prev) => !prev)}
                    className="flex items-center gap-2 w-full p-2 text-sm font-medium hover:bg-muted/50 rounded-md transition-colors"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${suggestionsExpanded ? '' : '-rotate-90'}`}
                    />
                    {t('processChains.edit.suggestions.title')}
                    <Badge variant="outline" size="sm">
                      {suggestedProcesses.length}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-normal ml-auto">
                      {t('processChains.edit.suggestions.hint')}
                    </span>
                  </button>
                  {suggestionsExpanded && (
                    <div className="flex flex-col gap-1 px-2 pb-2">
                      {suggestedProcesses.map((proc) => (
                        <div
                          key={proc._id}
                          className="flex items-center gap-2 rounded-sm border border-dashed p-2"
                        >
                          <span className="text-sm flex-1 min-w-0 truncate">{proc.name}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {proc.departmentName}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => handleAddSuggestion(proc._id)}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogShellBody>
          <DialogShellFooter
            secondary={{
              label: t('common.cancel'),
              onClick: () => onOpenChange(false)
            }}
            primary={{
              label: t('common.save'),
              onClick: handleSave,
              loading: editChain.isPending,
              disabled: graphState !== null
            }}
          />
        </DialogShell>
      </Dialog>

      <AutoDetectDialog
        open={autoDetectOpen}
        onOpenChange={setAutoDetectOpen}
        chainName={chainName}
        availableProcesses={availableProcesses}
        currentMappingIds={assignedIds}
        onConfirm={handleAutoDetectConfirm}
        processesLoading={processesLoading}
      />
    </>
  );
}
